/**
 * Shared Needs Action data fetching.
 *
 * Both the Needs Action page and the sidebar badge need the same query: the
 * Needs Action views assigned to the current user's role, OR'd together, and
 * the orders (and total count) that match any of them. Centralizing it keeps
 * the page and the nav badge in sync.
 *
 * Which views exist is not a lender-admin free-for-all: Reggora publishes a
 * catalog of templates and the admin assigns them to roles, so this endpoint
 * returns only what the caller's own role has been given. An empty result is
 * meaningful -- it means the page and nav item should not be shown at all.
 */

/**
 * Fetch the Needs Action views assigned to the current user's role.
 * @param {ReturnType<import('@app/apiClient').default>} api
 * @returns {Promise<Array<{id: string, name: string}>>}
 */
export const fetchNeedsActionViews = async (api) => {
  const viewsResponse = await api.get('lender/needs_action/views/')
  return viewsResponse.results || []
}

/**
 * How many cards a page of the queue holds.
 *
 * The orders endpoint defaults `limit` to 10, so an explicit value is required:
 * without one the page silently showed 10 of N while the badge reported the
 * true total.
 */
export const PAGE_SIZE = 25

/**
 * Fetch the needs-action views plus the orders/count matching ANY of them.
 * @param {ReturnType<import('@app/apiClient').default>} api
 * @param {{ limit?: number, offset?: number }} [options] limit caps the returned
 *   orders (the count is always the true total); pass a small limit when only
 *   the count is needed (e.g. the nav badge). offset pages through them.
 * @returns {Promise<{ views: Array, orders: Array, count: number }>}
 */
export const fetchNeedsActionData = async (api, { limit = PAGE_SIZE, offset = 0 } = {}) => {
  const views = await fetchNeedsActionViews(api)
  if (views.length === 0) {
    return { views: [], orders: [], count: 0 }
  }

  const params = {
    pipeline_views: views.map(v => v.id),
    // Multiple needs-action views are OR'd: an order shows if it matches ANY
    // assigned view, not all of them.
    pipeline_views_combinator: 'or',
    // Tag each order with the view(s) it matched (order.matched_pipeline_views).
    include_matched_views: true,
    offset,
    ordering: 'due_date',
    limit,
  }

  const ordersResponse = await api.get('order/full/', params)
  const orders = ordersResponse.results || ordersResponse.orders || []
  const count = typeof ordersResponse.count === 'number' ? ordersResponse.count : orders.length

  return { views, orders, count }
}
